import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import CreditCardComponent from './CreditCardComponent';
import SubscriptionStore from '../../../redux/subscriptions';
import useSubscription from '../../../hooks/subscriptions/useSubscription';
import { BUTTON_SIZE, LoaderButton } from '../../generic/Button';
import { icons } from '../../generic/Icon';
import type { FossaPlanName } from '../../../../shared/subscriptions';

const PaymentMethodContent = () => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const { updateSubscription } = useSubscription();

  const subscription = useSelector(SubscriptionStore.selectors.getSubscription);
  const status = useSelector(SubscriptionStore.selectors.getLoading);

  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [isInputDisabled, setInputDisabled] = useState(false);
  const [hasNewPayment, setHasNewPayment] = useState(false);

  if (!stripe || !elements) {
    throw new Error('Could not find stripe!');
  }

  // Fetch subscription if it doesn't exist or update hasPayment state
  useEffect(() => {
    if (!subscription) {
      dispatch(SubscriptionStore.actions.fetchSubscription());
    }
  }, [dispatch, subscription]);

  // Setup callback for submitting orders
  const onSubmitOrder = useCallback(async () => {
    if (!subscription) {
      return;
    }

    // We want to disable the inputs when submitting the card so they can't make changes.
    setInputDisabled(true);

    await updateSubscription({
      plan: subscription.plan as FossaPlanName,
    });

    // We can hide the editable element and re-enable because the action has been dipatched.
    // On successful update, the page will reload. But if the card is declined, for example, the page won't
    // reload so we need to allow input again.
    setIsEditingPayment(false);
    setInputDisabled(false);
  }, [subscription, updateSubscription]);

  return (
    <div className="payment-method-container">
      <CreditCardComponent
        isEditing={isEditingPayment}
        onChange={(change) => setHasNewPayment(change.complete)}
        onEdit={(isEditing) => setIsEditingPayment(isEditing)}
        paymentError={status.errors?.[0]}
        isDisabled={isInputDisabled}
      />
      {isEditingPayment && (
        <LoaderButton
          size={BUTTON_SIZE.LARGE}
          isDisabled={!hasNewPayment || isInputDisabled}
          isLoading={isInputDisabled}
          onClick={onSubmitOrder}
          icon={icons.check}
        >
          Update
        </LoaderButton>
      )}
    </div>
  );
};

export default PaymentMethodContent;
